import { ButtonSimple, IconNext, LoadingSpinner } from '@momentum-ui/react-collaboration';
import { DateTime } from 'luxon';
import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../../../contexts/ThemeContext';
import { formatDurationFromSeconds } from '../../../utils/dateUtils';
import { DislikeFeedbackDialog, IDislikeFeedbackResult } from './DislikeFeedbackDialog';
import { useAISummary } from './useAISummary';

/** Strip HTML tags to get plain text */
const stripHtml = (html: string): string => {
  const doc = new DOMParser().parseFromString(html, 'text/html');
  return doc.body.textContent || '';
};

type FeedbackState = 'none' | 'liked' | 'disliked';

const VISUALLY_HIDDEN_STYLES: React.CSSProperties = {
  position: 'absolute',
  width: '1px',
  height: '1px',
  padding: 0,
  margin: '-1px',
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

interface ICallHistoryExpandedRowProps {
  expandedRowId?: string;
  containerId: string;
  callerName: string;
  direction: string;
  startTime: string;
  durationSeconds: number;
  onCallSummaryClick: () => void;
  onTranscriptClick: () => void;
}

export const CallHistoryExpandedRow: React.FC<ICallHistoryExpandedRowProps> = ({
  expandedRowId,
  containerId,
  callerName,
  direction,
  startTime,
  durationSeconds,
  onCallSummaryClick,
  onTranscriptClick,
}) => {
  const { t, i18n } = useTranslation('WebexCallHistory');
  const locale = i18n.resolvedLanguage || i18n.language;
  const { summaryData, loading } = useAISummary(containerId);
  const { colors } = useTheme();
  const [feedback, setFeedback] = useState<FeedbackState>('none');
  const [showFeedbackDialog, setShowFeedbackDialog] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyButtonHovering, setCopyButtonHovering] = useState(false);
  const [copyAnnouncement, setCopyAnnouncement] = useState('');
  const copyTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const TEXT2 = 'var(--mds-color-theme-text-secondary-normal)';
  const notePreviewId = useId();
  const previewRegionLabel = callerName
    ? t('aiSummary.previewRegionLabel', {
        callerName,
        defaultValue: 'AI summary preview for {{callerName}}',
      })
    : t('aiSummary.previewRegionLabelGeneric', { defaultValue: 'AI summary preview' });

  useEffect(() => {
    return () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current); };
  }, []);

  const announce = useCallback((message: string) => {
    setCopyAnnouncement('');
    window.setTimeout(() => setCopyAnnouncement(message), 0);
  }, []);

  const handleFeedback = useCallback((type: 'liked' | 'disliked') => {
    if (type === 'disliked') {
      if (feedback === 'disliked') {
        setFeedback('none');
      } else {
        setFeedback('disliked');
        setShowFeedbackDialog(true);
      }
      return;
    }
    setFeedback(prev => (prev === type ? 'none' : type));
  }, [feedback]);

  const handleFeedbackSend = useCallback((result: IDislikeFeedbackResult) => {
    setShowFeedbackDialog(false);
  }, []);

  const handleFeedbackClose = useCallback(() => {
    setShowFeedbackDialog(false);
  }, []);

  const handleCopy = useCallback(async () => {
    const text = stripHtml(summaryData?.shortNote || '');
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopySuccess(true);
    if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
    copyTimerRef.current = setTimeout(() => setCopySuccess(false), 5000);
    announce(t('aiSummary.callNotesCopied', { defaultValue: 'Call notes copied to clipboard' }));
  }, [summaryData?.shortNote, announce, t]);

  const stop = (e: React.MouseEvent) => e.stopPropagation();

  const isOutgoing = direction === 'OUTGOING';
  const dirLabel = isOutgoing
    ? t('aiSummary.outgoing', { defaultValue: 'Outgoing' })
    : t('aiSummary.incoming', { defaultValue: 'Incoming' });
  const duration = formatDurationFromSeconds(durationSeconds || 0, locale);
  const startDateTime = DateTime.fromISO(startTime).setLocale(locale);
  const today = DateTime.now().startOf('day');
  const callDate = startDateTime.startOf('day');
  const daysAgo = today.diff(callDate, 'days').days;

  let dateLabel: string;
  if (daysAgo === 0) {
    dateLabel = t('aiSummary.today', { defaultValue: 'Today' });
  } else if (daysAgo === 1) {
    dateLabel = t('aiSummary.yesterday', { defaultValue: 'Yesterday' });
  } else if (daysAgo < 7) {
    dateLabel = t('aiSummary.daysAgo', { defaultValue: '{{count}} days ago', count: Math.floor(daysAgo) });
  } else {
    dateLabel = startDateTime.toLocaleString(DateTime.DATE_SHORT);
  }

  const dateSuffix = startDateTime.toLocaleString(DateTime.DATE_SHORT);
  const time = startDateTime.toLocaleString(DateTime.TIME_SIMPLE);

  if (loading) {
    return (
      <div
        id={expandedRowId}
        style={{
          padding: '10px 16px 10px 56px',
          background: colors.backgroundSecondary,
          borderBottom: `1px solid ${colors.outlineSecondary}`,
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          height: '100%',
          boxSizing: 'border-box' as const,
          display: 'flex',
          flexDirection: 'column' as const,
          justifyContent: 'center',
          overflow: 'hidden',
        }}
        data-testid="ai-expanded-row"
        onClick={stop}
        role="region"
        aria-label={previewRegionLabel}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '100%',
          color: colors.textSecondary,
          fontSize: '0.8125rem',
        }} role="status" aria-live="polite">
          <LoadingSpinner scale={24} />
          <span>{t('aiSummary.loadingSummary', { defaultValue: 'Loading summary...' })}</span>
        </div>
      </div>
    );
  }

  if (!summaryData || summaryData.status === 'error') {
    return (
      <div
        id={expandedRowId}
        style={{
          padding: '10px 16px 10px 56px',
          background: colors.backgroundSecondary,
          borderBottom: `1px solid ${colors.outlineSecondary}`,
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          height: '100%',
          boxSizing: 'border-box' as const,
          display: 'flex',
          flexDirection: 'column' as const,
          justifyContent: 'center',
          overflow: 'hidden',
        }}
        data-testid="ai-expanded-row"
        onClick={stop}
        role="region"
        aria-label={previewRegionLabel}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '100%',
          color: colors.textSecondary,
          fontSize: '0.8125rem',
        }} role="status" aria-live="polite">
          <span>{t('aiSummary.summaryUnavailable', { defaultValue: 'Summary unavailable' })}</span>
        </div>
      </div>
    );
  }

  if (summaryData.status === 'unavailable') {
    return (
      <div
        id={expandedRowId}
        style={{
          padding: '10px 16px 10px 56px',
          background: colors.backgroundSecondary,
          borderBottom: `1px solid ${colors.outlineSecondary}`,
          borderBottomLeftRadius: '12px',
          borderBottomRightRadius: '12px',
          height: '100%',
          boxSizing: 'border-box' as const,
          display: 'flex',
          flexDirection: 'column' as const,
          justifyContent: 'center',
          overflow: 'hidden',
        }}
        data-testid="ai-expanded-row"
        onClick={stop}
        role="region"
        aria-label={previewRegionLabel}
      >
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          height: '100%',
          color: colors.textSecondary,
          fontSize: '0.8125rem',
        }} role="status" aria-live="polite">
          <span>{t('aiSummary.summaryGenerating', { defaultValue: 'Summary is being generated...' })}</span>
        </div>
      </div>
    );
  }

  return (
    <div
      id={expandedRowId}
      style={{
        padding: '10px 16px 10px 56px',
        background: colors.backgroundSecondary,
        borderBottom: `1px solid ${colors.outlineSecondary}`,
        borderBottomLeftRadius: '12px',
        borderBottomRightRadius: '12px',
        height: '100%',
        boxSizing: 'border-box' as const,
        display: 'flex',
        flexDirection: 'column' as const,
        justifyContent: 'center',
        overflow: 'hidden',
      }}
      data-testid="ai-expanded-row"
      onClick={stop}
      role="region"
      aria-label={previewRegionLabel}
    >
      <span style={VISUALLY_HIDDEN_STYLES} aria-live="polite" aria-atomic="true">
        {copyAnnouncement}
      </span>

      {/* ---- date separator ---- */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '0.75rem',
        color: colors.textSecondary,
        marginBottom: '8px',
      }}>
        <span style={{
          flex: 1,
          height: '1px',
          background: colors.outlineSecondary,
        }} />
        <span style={{
          whiteSpace: 'nowrap' as const,
          padding: '0 4px',
          fontWeight: 500,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
        }}>
          {dateLabel}
          {dateSuffix && (
            <>
              <svg width="4" height="4" viewBox="0 0 4 4" xmlns="http://www.w3.org/2000/svg" style={{ margin: '0 4px', verticalAlign: 'middle' }}>
                <circle cx="2" cy="2" r="2" fill="currentColor" />
              </svg>
              {dateSuffix}
            </>
          )}
        </span>
        <span style={{
          flex: 1,
          height: '1px',
          background: colors.outlineSecondary,
        }} />
      </div>

      {/* ---- meta row ---- */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontSize: '0.75rem',
        color: colors.textSecondary,
        marginBottom: '8px',
      }}>
        <span style={{ fontWeight: 400, fontSize: '0.875rem', lineHeight: '1.25rem' }}>{dirLabel}</span>
        <span style={{ fontWeight: 400, fontSize: '0.875rem', lineHeight: '1.25rem', textAlign: 'center' }}>{duration}</span>
        <span style={{
          display: 'flex',
          alignItems: 'center',
          gap: '6px',
        }}>
          <span style={{ fontWeight: 400, fontSize: '0.875rem', lineHeight: '1.25rem', textAlign: 'right' }}>{time}</span>
          <IconNext name={isOutgoing ? 'outgoing-call-legacy' : 'incoming-call-legacy'} scale={16} />
        </span>
      </div>

      {/* ---- notes row with copy button (matching detailed summary page behavior) ---- */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '6px',
      }}>
        <span style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: '6px',
          color: colors.textSecondary,
        }}>
          <IconNext name="text-align-left" scale={14} />
          <span style={{
            fontWeight: 450,
            fontSize: '14px',
            lineHeight: '22px',
            letterSpacing: '-0.25px',
          }}>
            {t('aiSummary.callNotesPreviewLabel', { defaultValue: 'Call Notes' })}
          </span>
          <div onMouseEnter={() => setCopyButtonHovering(true)} onMouseLeave={() => setCopyButtonHovering(false)} style={{ display: 'inline-flex', alignItems: 'center' }}>
            <ButtonSimple
              style={{
                background: copyButtonHovering ? 'rgba(0,0,0,0.05)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: '4px',
                display: 'inline-flex',
                alignItems: 'center',
                position: 'relative' as const,
                zIndex: 2,
                minWidth: 24,
                minHeight: 24,
                borderRadius: '4px',
                color: copySuccess ? 'var(--mds-color-theme-text-accent-normal, #1170CF)' : TEXT2,
                transition: 'background-color 0.15s ease, color 0.15s ease',

              }}
              onPress={handleCopy}
              aria-label={t('aiSummary.copyCallNotesPreview', { defaultValue: 'Copy call notes preview to clipboard' })}
              title={t('aiSummary.copyToClipboard', { defaultValue: 'Copy to clipboard' })}
            >
              <span style={{ pointerEvents: 'none' as const, display: 'inline-flex' }}>
                {copySuccess ? <IconNext name="check" scale={16} /> : <IconNext name="copy" scale={16} />}
              </span>
            </ButtonSimple>
          </div>
        </span>

        {/* AI-generated badge with feedback buttons */}
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            fontSize: '14px',
            lineHeight: '22px',
            fontWeight: 400,
            color: colors.textSecondary,
          }}
          role="group"
          aria-label={t('aiSummary.callNotesPreviewFeedback', { defaultValue: 'Call notes preview feedback' })}
        >
          <span>{t('aiSummary.aiGenerated', { defaultValue: 'AI-generated' })}</span>
          <svg width="4" height="4" viewBox="0 0 4 4" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
            <circle cx="2" cy="2" r="2" fill="currentColor" />
          </svg>
          <ButtonSimple
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              display: 'inline-flex',
              alignItems: 'center',
              color: feedback === 'liked' ? 'var(--mds-color-theme-text-accent-normal, #1170CF)' : colors.textSecondary,
            }}
            onPress={() => handleFeedback('liked')}
            aria-label={t('aiSummary.likeCallNotesPreview', { defaultValue: 'Like call notes preview' })}
            aria-pressed={feedback === 'liked'}
            title={t('aiSummary.like', { defaultValue: 'Like' })}
          >
            <span style={{ pointerEvents: 'none' as const }}><IconNext name="like" scale={14} /></span>
          </ButtonSimple>
          <ButtonSimple
            style={{
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              padding: '2px',
              display: 'inline-flex',
              alignItems: 'center',
              color: feedback === 'disliked' ? 'var(--mds-color-theme-text-accent-normal, #1170CF)' : colors.textSecondary,
            }}
            onPress={() => handleFeedback('disliked')}
            aria-label={t('aiSummary.dislikeCallNotesPreview', { defaultValue: 'Dislike call notes preview' })}
            aria-pressed={feedback === 'disliked'}
            title={t('aiSummary.dislike', { defaultValue: 'Dislike' })}
          >
            <span style={{ pointerEvents: 'none' as const }}><IconNext name="dislike" scale={14} /></span>
          </ButtonSimple>
        </span>
      </div>

      {/* ---- note text ---- */}
      <p id={notePreviewId} style={{
        fontSize: '0.875rem',
        lineHeight: '1.25rem',
        fontWeight: 400,
        color: colors.textSecondary,
        margin: '0 0 10px',
        display: '-webkit-box',
        WebkitLineClamp: 3,
        WebkitBoxOrient: 'vertical' as const,
        overflow: 'hidden',
      }}>
        {stripHtml(summaryData.shortNote)}
      </p>

      {/* ---- action buttons ---- */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
      }} role="group" aria-label={t('aiSummary.actionsGroup', { defaultValue: 'AI summary actions' })}>
        <ButtonSimple
          style={{
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
            color: colors.textPrimary,
            height: '24px',
            padding: '0 12px',
            border: `1px solid var(--mds-color-theme-outline-input-normal, #555)`,
            borderRadius: '4px',
            background: 'transparent',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box' as const,
          }}
          onPress={onCallSummaryClick}
          aria-label={callerName
            ? t('aiSummary.openCallSummaryFor', {
                callerName,
                defaultValue: 'Open call summary for {{callerName}}',
              })
            : t('aiSummary.openCallSummary', { defaultValue: 'Open call summary' })}
          aria-describedby={notePreviewId}
        >
          {t('aiSummary.callSummaryButton', { defaultValue: 'Call Summary' })}
        </ButtonSimple>
        <ButtonSimple
          style={{
            fontSize: '0.875rem',
            fontWeight: 400,
            lineHeight: '1.25rem',
            color: colors.textPrimary,
            height: '24px',
            padding: '0 12px',
            border: `1px solid var(--mds-color-theme-outline-input-normal, #555)`,
            borderRadius: '4px',
            background: 'transparent',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxSizing: 'border-box' as const,
          }}
          onPress={onTranscriptClick}
          aria-label={callerName
            ? t('aiSummary.openTranscriptFor', {
                callerName,
                defaultValue: 'Open transcript for {{callerName}}',
              })
            : t('aiSummary.openTranscript', { defaultValue: 'Open transcript' })}
        >
          {t('aiSummary.transcriptButton', { defaultValue: 'Transcript' })}
        </ButtonSimple>
      </div>

      <DislikeFeedbackDialog
        isOpen={showFeedbackDialog}
        onClose={handleFeedbackClose}
        onSend={handleFeedbackSend}
      />
    </div>
  );
};
